import { z } from "zod/v4";

import {
  CreateExperienceAnalysisDtoDescriptions,
  EstimatedTestLengthRangeDescriptions,
  ExperienceAnalysisDtoDescriptions,
} from "../descriptions/experience-analysis-dto.describe";
import { assertDescriptions } from "../descriptions/types";
import { AnalyticsViewTypeList } from "../schemas/analytics/base";

export const EstimatedTestLengthRangeSchema = z
  .object({
    low: z
      .number()
      .nullish()
      .describe(EstimatedTestLengthRangeDescriptions.low),
    high: z
      .number()
      .nullish()
      .describe(EstimatedTestLengthRangeDescriptions.high),
  })
  .describe(EstimatedTestLengthRangeDescriptions._schema);

export type EstimatedTestLengthRangeType = z.infer<
  typeof EstimatedTestLengthRangeSchema
>;

export const ExperienceAnalysisDtoSchema = z.object({
  id: z.string().describe(ExperienceAnalysisDtoDescriptions.id),
  experienceId: z
    .string()
    .describe(ExperienceAnalysisDtoDescriptions.experienceId),
  estimatedTestLengthRange: EstimatedTestLengthRangeSchema.optional().describe(
    ExperienceAnalysisDtoDescriptions.estimatedTestLengthRange
  ),
  analyticsViewType: z
    .enum(AnalyticsViewTypeList)
    .describe(ExperienceAnalysisDtoDescriptions.analyticsViewType),
});

export type ExperienceAnalysisDto = z.infer<typeof ExperienceAnalysisDtoSchema>;

export const CreateExperienceAnalysisSchema =
  ExperienceAnalysisDtoSchema.extend({
    id: z
      .uuid()
      .optional()
      .describe(CreateExperienceAnalysisDtoDescriptions.id),
    experienceId: z
      .uuid()
      .optional()
      .describe(CreateExperienceAnalysisDtoDescriptions.experienceId),
  });

export const ExternalCreateExperienceAnalysisSchema =
  CreateExperienceAnalysisSchema.omit({
    id: true,
    experienceId: true,
    estimatedTestLengthRange: true,
  }).extend({
    analyticsViewType: z
      .enum(AnalyticsViewTypeList)
      .default("none")
      .describe(CreateExperienceAnalysisDtoDescriptions.analyticsViewType),
  });

// Used by PutExperienceRequestBodySchema (internal). Keeps id/experienceId as optional
// since the mapper provides them. External-facing schemas use ExternalCreateExperienceAnalysisSchema instead.
export const ExternalPutExperienceAnalysisSchema =
  CreateExperienceAnalysisSchema.omit({ estimatedTestLengthRange: true });

export type CreateExperienceAnalysis = z.infer<
  typeof CreateExperienceAnalysisSchema
>;

export const ExternalApiExperienceAnalysisDtoSchema =
  ExperienceAnalysisDtoSchema.pick({
    analyticsViewType: true,
  });

export type ExternalApiExperienceAnalysisDto = z.infer<
  typeof ExternalApiExperienceAnalysisDtoSchema
>;

// Compile-time checks: description objects must cover all schema keys
assertDescriptions<
  typeof EstimatedTestLengthRangeDescriptions,
  EstimatedTestLengthRangeType
>();
assertDescriptions<
  typeof ExperienceAnalysisDtoDescriptions,
  ExperienceAnalysisDto
>();
